/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { AbsoluteCellRange } from './AbsoluteCellRange';
import { absolutizeDependencies } from './absolutizeDependencies';
import { CellError, ErrorType } from './Cell';
import { SimpleRangeValue } from './interpreter/SimpleRangeValue';
import { ContentChanges } from './ContentChanges';
import { FormulaCellVertex, MatrixVertex, RangeVertex } from './DependencyGraph';
import { ErrorMessage } from './error-message';
import { Interpreter } from './interpreter/Interpreter';
import { EmptyValue, getRawValue } from './interpreter/InterpreterValue';
import { Matrix } from './Matrix';
import { StatType } from './statistics';
export class Evaluator {
    constructor(dependencyGraph, columnSearch, config, stats, dateHelper, numberLiteralsHelper, functionRegistry, namedExpressions, serialization) {
        this.dependencyGraph = dependencyGraph;
        this.columnSearch = columnSearch;
        this.config = config;
        this.stats = stats;
        this.dateHelper = dateHelper;
        this.numberLiteralsHelper = numberLiteralsHelper;
        this.functionRegistry = functionRegistry;
        this.namedExpressions = namedExpressions;
        this.serialization = serialization;
        this.interpreter = new Interpreter(this.dependencyGraph, this.columnSearch, this.config, this.stats, this.dateHelper, this.numberLiteralsHelper, this.functionRegistry, this.namedExpressions, this.serialization);
    }
    run() {
        this.stats.start(StatType.TOP_SORT);
        const { sorted, cycled } = this.dependencyGraph.topSortWithScc();
        this.stats.end(StatType.TOP_SORT);
        this.stats.measure(StatType.EVALUATION, () => {
            this.recomputeFormulas(cycled, sorted);
        });
    }
    partialRun(vertices) {
        const changes = new ContentChanges();
        this.stats.measure(StatType.EVALUATION, () => {
            this.dependencyGraph.graph.getTopSortedWithSccSubgraphFrom(vertices, (vertex) => {
                if (vertex instanceof FormulaCellVertex) {
                    const address = vertex.getAddress(this.dependencyGraph.lazilyTransformingAstService);
                    const formula = vertex.getFormula(this.dependencyGraph.lazilyTransformingAstService);
                    const currentValue = vertex.isComputed() ? vertex.getCellValue() : null;
                    const newCellValue = this.evaluateAstToCellValue(formula, address);
                    vertex.setCellValue(newCellValue);
                    if (newCellValue !== currentValue) {
                        changes.addChange(newCellValue, address);
                        this.columnSearch.change(getRawValue(currentValue), getRawValue(newCellValue), address);
                        return true;
                    }
                    return false;
                }
                else if (vertex instanceof MatrixVertex && vertex.isFormula()) {
                    const address = vertex.getAddress();
                    const formula = vertex.getFormula();
                    const currentValue = vertex.isComputed() ? vertex.getCellValue() : null;
                    const newCellValue = this.evaluateAstToRangeValue(formula, address);
                    if (newCellValue instanceof SimpleRangeValue) {
                        const newCellMatrix = new Matrix(newCellValue.rawNumbers());
                        vertex.setCellValue(newCellMatrix);
                        changes.addMatrixChange(newCellMatrix, address);
                        this.columnSearch.change(currentValue, newCellMatrix, address);
                    }
                    else {
                        vertex.setErrorValue(newCellValue);
                        changes.addChange(newCellValue, address);
                        this.columnSearch.change(currentValue, newCellValue, address);
                    }
                    return true;
                }
                else if (vertex instanceof RangeVertex) {
                    vertex.clearCache();
                    return true;
                }
                else {
                    return true;
                }
            }, (vertex) => {
                if (vertex instanceof RangeVertex) {
                    vertex.clearCache();
                }
                else if (vertex instanceof FormulaCellVertex) {
                    const address = vertex.getAddress(this.dependencyGraph.lazilyTransformingAstService);
                    this.columnSearch.remove(getRawValue(vertex.valueOrNull()), address);
                    const error = new CellError(ErrorType.CYCLE, undefined, vertex.address);
                    vertex.setCellValue(error);
                    changes.addChange(error, vertex.address);
                }
            });
        });
        return changes;
    }
    destroy() {
        this.interpreter.destroy();
    }
    runAndForget(ast, address, dependencies) {
        const tmpRanges = [];
        for (const dep of absolutizeDependencies(dependencies, address)) {
            if (dep instanceof AbsoluteCellRange) {
                const range = dep;
                if (this.dependencyGraph.getRange(range.start, range.end) === undefined) {
                    const rangeVertex = new RangeVertex(range);
                    this.dependencyGraph.rangeMapping.setRange(rangeVertex);
                    tmpRanges.push(rangeVertex);
                }
            }
        }
        const ret = this.evaluateAstToCellValue(ast, address);
        tmpRanges.forEach((rangeVertex) => {
            this.dependencyGraph.rangeMapping.removeRange(rangeVertex);
        });
        return ret;
    }
    /**
     * Recalculates formulas in the topological sort order
     */
    recomputeFormulas(cycled, sorted) {
        cycled.forEach((vertex) => {
            if (vertex instanceof FormulaCellVertex) {
                vertex.setCellValue(new CellError(ErrorType.CYCLE, undefined, vertex.address));
            }
        });
        sorted.forEach((vertex) => {
            if (vertex instanceof FormulaCellVertex) {
                const address = vertex.getAddress(this.dependencyGraph.lazilyTransformingAstService);
                const formula = vertex.getFormula(this.dependencyGraph.lazilyTransformingAstService);
                const newCellValue = this.evaluateAstToCellValue(formula, address);
                vertex.setCellValue(newCellValue);
                this.columnSearch.add(getRawValue(newCellValue), address);
            }
            else if (vertex instanceof MatrixVertex && vertex.isFormula()) {
                const address = vertex.getAddress();
                const formula = vertex.getFormula();
                const newCellValue = this.evaluateAstToRangeValue(formula, address);
                if (newCellValue instanceof SimpleRangeValue) {
                    const newCellMatrix = new Matrix(newCellValue.rawNumbers());
                    vertex.setCellValue(newCellMatrix);
                    this.columnSearch.add(newCellMatrix, address);
                }
                else {
                    vertex.setErrorValue(newCellValue);
                    this.columnSearch.add(newCellValue, address);
                }
            }
            else if (vertex instanceof RangeVertex) {
                vertex.clearCache();
            }
        });
    }
    evaluateAstToCellValue(ast, formulaAddress) {
        const interpreterValue = this.interpreter.evaluateAst(ast, formulaAddress);
        if (interpreterValue instanceof SimpleRangeValue) {
            return interpreterValue;
        }
        else if (interpreterValue === EmptyValue && this.config.evaluateNullToZero) {
            return 0;
        }
        else {
            return interpreterValue;
        }
    }
    evaluateAstToRangeValue(ast, formulaAddress) {
        const interpreterValue = this.interpreter.evaluateAst(ast, formulaAddress);
        if (interpreterValue instanceof CellError) {
            return interpreterValue;
        }
        else if (interpreterValue instanceof SimpleRangeValue && interpreterValue.hasOnlyNumbers()) {
            return interpreterValue;
        }
        else {
            return new CellError(ErrorType.VALUE, ErrorMessage.CellRangeExpected);
        }
    }
}
